import dayjs from 'dayjs';
import type { FormDataOf, FormDataValue } from '../../../../../types/templates';
import { disneyEspnVS } from '..';
import { SHARED_ESPN_CONFIG } from '../../shared';

type FormData = FormDataOf<typeof disneyEspnVS>;
type RegionKey = keyof typeof SHARED_ESPN_CONFIG.regionOffsets;

const HHMM = 'HH:mm';
const fmt = (d: dayjs.Dayjs) => d.format(HHMM);

const recomputeTimesFromBase = (baseHHMM: string, data: FormData): Partial<FormData> => {
  const base = dayjs(baseHHMM, HHMM);
  const out: Partial<FormData> = {};
  const OFF = SHARED_ESPN_CONFIG.regionOffsets;

  if (data.region2) out.time2 = fmt(base.add(OFF[data.region2 as RegionKey] ?? 0, 'hour'));
  if (data.region3) out.time3 = fmt(base.add(OFF[data.region3 as RegionKey] ?? 0, 'hour'));
  if (data.region4) out.time4 = fmt(base.add(OFF[data.region4 as RegionKey] ?? 0, 'hour'));
  return out;
};

const computeSingleTime = (baseTime: string, regionOffset: number): string => {
  const base = dayjs(baseTime, HHMM);
  return fmt(base.add(regionOffset, 'hour'));
};

const applyESPNDynamicOptions = (fieldKey: string, _value: FormDataValue, _formData: FormData): Partial<FormData> => {
  const updates: Partial<FormData> = {};
  if (fieldKey === 'sport') {
    updates.league = '';
    updates.team1 = '';
    updates.team2 = '';
    updates.tournament = '';
  }
  return updates;
};

export const buildESPNBannerType = (formData: FormData): string => {
  const { sport, league } = formData;
  if (!sport || !league) return 'espn/espn-vs';
  return `espn/espn-vs/${sport}/${league}/vertical`;
};

export const applyESPNLogic = (fieldKey: string, value: FormDataValue, formData: FormData): Partial<FormData> => {
  const updates: Partial<FormData> = {};

  Object.assign(updates, applyESPNDynamicOptions(fieldKey, value, formData));

  if (fieldKey === 'autoSyncTimes' && value && formData.time1) {
    Object.assign(updates, recomputeTimesFromBase(formData.time1, formData));
  }

  if (fieldKey === 'enableRegion4' && !value) {
    updates.region4 = undefined;
    updates.time4 = undefined;
  }

  if (formData.autoSyncTimes && fieldKey === 'time1' && value) {
    Object.assign(updates, recomputeTimesFromBase(value as string, formData));
  }

  if (
    formData.autoSyncTimes &&
    formData.time1 &&
    (fieldKey === 'region2' || fieldKey === 'region3' || fieldKey === 'region4')
  ) {
    const keyToTime = fieldKey.replace('region', 'time') as 'time2' | 'time3' | 'time4';
    const offset = SHARED_ESPN_CONFIG.regionOffsets[value as RegionKey] ?? 0;
    updates[keyToTime] = computeSingleTime(formData.time1, offset);
  }

  return updates;
};
